import { ScorioSocket } from "@/api/ws";
import { getIdToken } from "@/firebase";
import { judgeSessionToken } from "@/stores/judgeSession";
import { JamQueue } from "@/components/operations/JamQueue";
import {
  groupHeats,
  orderedHeats,
  scheduleItems,
  scheduleLabel,
} from "@/components/operations/navigation";
import { RoundActions } from "@/components/operations/RoundActions";
import { rerideLabel } from "@/components/run/model";
import { RepairClock } from "@/components/run/RepairClock";
/** A phone role pad. Administrative shell and console grids do not belong here. */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import { playDing } from "@/utils/ding";
import { CompleteCompetitionSheet } from "@/components/run/CompleteCompetitionSheet";
import { Link, useParams } from "react-router-dom";
import { api, ApiError, type RequestOptions } from "@/api/client";
import { officialActions as sharedOfficialActions } from "@/components/official/actions";
import {
  createRunQueue,
  indexedRunStore,
  type RunOp,
  type RunReceipt,
  type RunOperation,
} from "@/api/runQueue";
import {
  projectRunState,
  projectAttemptMove,
  type RunHeat,
  type RunRider,
  type RunState,
} from "@/components/run/model";
import { ModifierSheet } from "@/components/run/ModifierSheet";
import { RerideSheet } from "@/components/run/RerideSheet";
import { HeatRidersSheet } from "@/components/run/HeatRidersSheet";
import { FallSheet } from "@/components/run/FallSheet";
import { RunSheet } from "@/components/run/RunSheet";
import { useOnline } from "@/hooks/useOnline";
import { useAuthStore } from "@/stores/auth";
import "./RunDay.css";

export interface RunDayTransport {
  get: <T>(path: string) => Promise<T>;
  delete: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: RequestOptions<T>) => Promise<T>;
}
export interface RunDayIntegration {
  /** Only provide shared sheets whose real server commands are integrated. */
  officialActions?: (
    heat: RunHeat,
    rider?: RunRider,
  ) => {
    label: string;
    render: (close: () => void, refreshed: () => Promise<void>) => ReactNode;
  }[];
  heatRiders?: (heat: RunHeat, close: () => void, refreshed: () => Promise<void>) => ReactNode;
  completeCompetition?: (close: () => void, refreshed: () => Promise<void>) => ReactNode;
}
const store = indexedRunStore();
type Sheet =
  | {
      kind: "end" | "menu" | "heat" | "abandon" | "reopen" | "excuse";
      rider?: never;
      seat?: { seat_id: string; judge_number: number };
    }
  | { kind: "rider"; rider: RunRider };
export function RunDayPage({
  transport = api,
  integration = {},
  principalKey,
  onSignOut,
}: {
  transport?: RunDayTransport;
  integration?: RunDayIntegration;
  principalKey?: string;
  onSignOut?: () => Promise<void>;
} = {}) {
  const { competitionId = "" } = useParams();
  const userId = useAuthStore((s) => s.user?.id);
  const identity = principalKey ?? (userId ? `user:${userId}` : null);
  const base = `/api/competitions/${competitionId}`;
  const [server, setServer] = useState<RunState | null>(null);
  const [ops, setOps] = useState<RunOperation[]>([]);
  const [selected, setSelected] = useState("");
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const online = useOnline();
  const [working, setBusy] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const selectedAreaDisabled =
    server?.capabilities?.areas === false &&
    !!server.heats.find((row) => row.id === selected)?.area_id;
  const busy = working || permissionDenied || selectedAreaDisabled;
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [custom, setCustom] = useState<ReactNode>(null);
  const [reason, setReason] = useState("");
  const [sound, setSound] = useState(false);
  const [area, setArea] = useState("");
  const areaInitialized = useRef(false);
  const [undo, setUndo] = useState<{
    label: string;
    op: RunOp;
    heatId: string;
    payload: Record<string, unknown>;
  } | null>(null);
  const mounted = useRef(true);
  const loadSequence = useRef(0);
  const cacheKey = identity ? `scorio.run-state:${identity}:${competitionId}` : null;
  useEffect(() => {
    if (server && cacheKey) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(server));
      } catch {
        setError("Could not cache this screen. Keep it open while offline.");
      }
    }
  }, [server, cacheKey]);
  const queuePrincipal = server?.principal;
  const queue = useMemo(
    () =>
      queuePrincipal
        ? createRunQueue(
            { principal: queuePrincipal, competitionId },
            {
              store,
              send: (batch) => transport.post<RunReceipt[]>(`${base}/run-replay`, batch),
            },
          )
        : null,
    [queuePrincipal, competitionId, base, transport],
  ); // stable principal keeps queue ownership
  const refresh = useCallback(async () => {
    const sequence = ++loadSequence.current;
    let next: RunState;
    try {
      next = await transport.get<RunState>(`${base}/run-state`);
    } catch (cause) {
      if (
        mounted.current &&
        sequence === loadSequence.current &&
        cause instanceof ApiError &&
        [401, 403].includes(cause.status)
      )
        setPermissionDenied(true);
      throw cause;
    }
    if (!mounted.current || sequence !== loadSequence.current) return;
    setPermissionDenied(false);
    next.clock_received_at = Date.now();
    next.heats.forEach((heat) => {
      if (heat.timer) heat.timer.client_at = Date.now();
    });
    setServer(next);
    if (!areaInitialized.current && next.features.areas) {
      setArea(next.heats.find((h) => h.status === "live")?.area_id ?? next.heats[0]?.area_id ?? "");
      areaInitialized.current = true;
    }
    setError("");

    setSelected((current) =>
      next.heats.some((h) => h.id === current)
        ? current
        : ((next.heats.find((h) => h.status === "live") ?? next.heats[0])?.id ?? ""),
    );
  }, [base, transport]);
  useEffect(() => {
    mounted.current = true;
    setServer(null);
    setOps([]);
    setSelected("");
    setArea("");
    areaInitialized.current = false;
    if (cacheKey) {
      try {
        const saved = localStorage.getItem(cacheKey);
        if (saved) {
          const parsed: RunState = JSON.parse(saved);
          setServer(parsed);
          if (parsed.features.areas) {
            setArea(
              parsed.heats.find((h) => h.status === "live")?.area_id ??
                parsed.heats[0]?.area_id ??
                "",
            );
            areaInitialized.current = true;
          }
          setSelected(
            parsed.heats.find((h) => h.status === "live")?.id ?? parsed.heats[0]?.id ?? "",
          );
        }
      } catch {
        /* The server remains authoritative if a cache was evicted or corrupted. */
      }
    }
    void refresh().catch((e) => setError(e.message));
    return () => {
      mounted.current = false;
    };
  }, [cacheKey, refresh]);
  useEffect(() => {
    if (!online || !queuePrincipal) return;
    const wsBase =
      import.meta.env.VITE_WS_URL ??
      `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}`;
    const socket = new ScorioSocket(
      `${wsBase}${base}/ws?scope_area=true${area ? `&area_id=${encodeURIComponent(area)}` : ""}`,
      {
        getToken: async () =>
          principalKey ? judgeSessionToken(competitionId, principalKey) : getIdToken(),
      },
    );
    const unsubscribe = socket.subscribe((message) => {
      if (
        [
          "live_state",
          "heats_update",
          "schedule_update",
          "round_status",
          "progression_applied",
          "jam_queue_update",
          "rider_attempt_update",
          "attempt_update",
        ].includes(message.type as string)
      )
        void refresh().catch(() => {});
    });
    socket.connect();
    return () => {
      unsubscribe();
      socket.disconnect();
    };
  }, [online, queuePrincipal, area, base, competitionId, principalKey, refresh]);
  const sync = useCallback(async () => {
    if (!queue || !navigator.onLine) return;
    try {
      const receipts = await queue.flush();
      const conflict = receipts.find((r) => r.result === "conflict");
      if (conflict?.reason === "attempts_open") {
        setSheet({ kind: "end" });
        setNotice("");
      } else if (conflict)
        setNotice(
          `Server conflict: ${conflict.reason?.replaceAll("_", " ")}. Local actions for this heat were discarded.`,
        );
      const remaining = await queue.items();
      setServer((current) => {
        if (!current) return current;
        const next = structuredClone(current);
        for (const receipt of receipts) {
          if (!receipt.server_state) continue;
          const snapshot = receipt.server_state;
          const target = next.heats.find((h) => h.id === snapshot.id);
          if (!target) continue;
          target.status = snapshot.status;
          target.version = snapshot.version;
          target.riders.forEach((r) => {
            if (snapshot.current_attempts[r.heat_rider_id] !== undefined)
              r.current_attempt = snapshot.current_attempts[r.heat_rider_id];
          });
          if (target.timer && ["complete", "abandoned"].includes(target.status))
            target.timer.running = false;
        }
        return next;
      });
      setOps(remaining);
      await refresh();
      return receipts;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not sync. Actions remain queued.");
    }
  }, [queue, refresh]);
  useEffect(() => {
    if (!queue) return;
    void queue
      .items()
      .then(setOps)
      .catch((e) => setError(e.message));
    if (online) void sync();
    const timer = window.setInterval(() => {
      if (navigator.onLine) void sync();
    }, 5000);
    return () => clearInterval(timer);
  }, [queue, online, sync]);
  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(null), 5000);
    return () => clearTimeout(timer);
  }, [undo]);
  const state = server ? projectRunState(server, ops, tick) : null;
  const heat = state?.heats.find(
    (h) => h.id === selected && (!state.features.areas || (h.area_id ?? "") === area),
  );
  const roles = state?.roles ?? [];
  const admin = roles.includes("admin");
  const official = roles.some((r) => ["admin", "head_judge", "chief_judge"].includes(r));
  const marshal = roles.some((r) => ["admin", "producer", "starter"].includes(r));
  const canAbandon = roles.some((r) => ["admin", "chief_judge", "starter"].includes(r));
  const close = () => {
    setSheet(null);
    setReason("");
  };
  const command = async (op: RunOp, payload: Record<string, unknown> = {}, heatId = heat?.id) => {
    if (!queue || !heatId || busy) return;
    if (
      server?.features.areas &&
      server.capabilities?.areas === false &&
      server.heats.find((row) => row.id === heatId)?.area_id
    ) {
      setError(
        "Areas require Club Unlimited or an Event pass. Review the current plan before continuing.",
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const item = await queue.enqueue(op, heatId, payload);
      if (["focus", "status", "fall", "excuse"].includes(op) && !payload.undo_client_id) {
        const prior = heat?.riders.find((r) => r.heat_rider_id === payload.heat_rider_id);
        setUndo({
          label: `${op === "focus" ? "Rider selected" : op === "status" ? "Status changed" : op === "fall" ? "Fall recorded" : "Judge excused"}.`,
          op,
          heatId,
          payload: {
            undo_client_id: item.client_id,
            ...(op === "focus" ? { heat_rider_id: heat?.focus_heat_rider_id ?? null } : {}),
            ...(op === "status"
              ? { heat_rider_id: payload.heat_rider_id, status: prior?.status ?? "ok" }
              : {}),
            ...(op === "fall" ? { heat_rider_id: payload.heat_rider_id } : {}),
          },
        });
      }
      if (sound && ["start", "focus", "end"].includes(op)) playDing();
      setOps(await queue.items());
      close();
      const receipts = navigator.onLine ? await sync() : undefined;
      const receipt = receipts?.find((r) => r.client_id === item.client_id);
      if (receipt?.result === "conflict") {
        if (op === "jam-append")
          throw new ApiError(409, "The heat changed. Review the queue before adding this attempt.");
        return;
      }
      return { ...item, acceptedState: receipt?.server_state };
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action could not be saved on this device.");
      if (op === "jam-append") throw e;
    } finally {
      setBusy(false);
    }
  };
  const move = async (rider: RunRider, advancing: boolean) => {
    if (!heat) return;
    const projected = structuredClone(rider);
    projectAttemptMove(heat, projected, advancing);
    const item = await command(advancing ? "attempt-advance" : "attempt-revert", {
      heat_rider_id: rider.heat_rider_id,
      expected_current_attempt: rider.current_attempt,
    });
    if (heat && item)
      setUndo({
        label: advancing ? "Attempt advanced" : "Attempt reverted",
        op: advancing ? "attempt-revert" : "attempt-advance",
        heatId: heat.id,
        payload: {
          heat_rider_id: rider.heat_rider_id,
          undo_client_id: item.client_id,
          expected_current_attempt:
            item.acceptedState?.current_attempts[rider.heat_rider_id] ?? projected.current_attempt,
          restore_current_attempt: rider.current_attempt,
        },
      });
  };
  if (!state)
    return (
      <main className="run-day">
        <h1>Run the day</h1>
        <p role={error ? "alert" : "status"}>{error || "Loading heats…"}</p>
      </main>
    );
  const filtered = orderedHeats(
    state.heats.filter((h) => !state.features.areas || (h.area_id ?? "") === area),
  );
  const roundsEnabled = !!state.features.rounds_manual;
  const capabilities = state.capabilities ?? state.features;
  const allComplete =
    state.heats.length > 0 &&
    state.heats.every((h) => ["complete", "abandoned"].includes(h.status));
  const nextHeat =
    filtered
      .slice(filtered.findIndex((h) => h.id === selected) + 1)
      .find((h) => h.status === "pending") ?? filtered.find((h) => h.status === "pending");
  const eligible = heat?.riders.filter((r) => r.status === "ok") ?? [];
  const focusIndex = eligible.findIndex((r) => r.heat_rider_id === heat?.focus_heat_rider_id);
  const nextRider = eligible[focusIndex + 1];
  const actionsFor = (target: RunHeat, rider?: RunRider) =>
    integration.officialActions?.(target, rider) ??
    sharedOfficialActions(competitionId, target, transport, rider);
  const officialActions = official && heat ? actionsFor(heat) : [];
  const showHeatMenu =
    heat &&
    ((canAbandon && ["pending", "live"].includes(heat.status)) ||
      officialActions.length > 0 ||
      (state.features.timer && heat.timer && heat.status === "live"));

  return (
    <main className="run-day">
      <header className="run-heading">
        <div>
          <h1>Run the day</h1>
          <p>{state.name}</p>
        </div>
        <button aria-label="Run the day menu" onClick={() => setSheet({ kind: "menu" })}>
          ⋯
        </button>
      </header>
      <div aria-live="polite" className="run-announcements">
        {(!online || ops.length > 0) && (
          <p className="run-banner">
            {online ? "Syncing" : "Offline"} — {ops.length} actions queued. Public results update
            after reconnect.
          </p>
        )}
        {notice && <p className="run-banner run-conflict">{notice}</p>}
        {selectedAreaDisabled && (
          <p className="run-banner">
            This heat uses an area. Areas require the enabled module and Club Unlimited or an Event
            pass.
          </p>
        )}
        {error && (
          <p role="alert" className="run-error">
            {error}
          </p>
        )}
        {undo && (
          <p className="run-banner">
            {undo.label}{" "}
            <button
              onClick={() => {
                const action = undo;
                setUndo(null);
                void command(action.op, action.payload, action.heatId);
              }}
            >
              Undo
            </button>
          </p>
        )}
      </div>
      {state.features.areas && (
        <label className="run-picker">
          Area
          <select
            value={area}
            onChange={(e) => {
              const value = e.target.value;
              setArea(value);
              const first = orderedHeats(state.heats).find((h) => (h.area_id ?? "") === value);
              setSelected(first?.id ?? "");
            }}
          >
            <option value="">Unassigned area</option>
            {state.areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="run-picker">
        Heat
        <select
          aria-label="Heat"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={!filtered.length}
        >
          {!filtered.length && <option value="">No heats yet</option>}
          {groupHeats(filtered).map((group) => {
            const round = state.rounds?.find((row) => row.id === group.roundId);
            return (
              <optgroup
                key={`${group.divisionId}:${group.roundId}`}
                label={[group.heats[0]?.division ?? "Heats", roundsEnabled && round?.name]
                  .filter(Boolean)
                  .join(" · ")}
              >
                {group.heats.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name} · {h.status}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
      </label>
      {roundsEnabled && (
        <div className="run-rounds" aria-label="Rounds">
          {groupHeats(filtered)
            .filter((group) => group.roundId && group.heats.some((row) => row.id === selected))
            .map((group) => {
              const round = state.rounds?.find((row) => row.id === group.roundId);
              if (!round) return null;
              const wholeRound = state.heats.filter((row) => row.round_id === round.id);
              return (
                <section className="run-round-row" key={round.id}>
                  <strong>{round.name}</strong>
                  <p className="run-status">{round.status}</p>
                  <RoundActions
                    competitionId={competitionId}
                    principalKey={state.principal}
                    competitionVersion={state.version ?? 0}
                    revision={`${state.version}:${round.version}`}
                    roles={roles}
                    manualEnabled={!!capabilities.rounds_manual}
                    rulesEnabled={!!state.features.rounds_rules}
                    rulesAllowed={!!capabilities.rounds_rules}
                    roundId={round.id}
                    complete={
                      wholeRound.length > 0 && wholeRound.every((row) => row.status === "complete")
                    }
                    allowAbandon
                    transport={transport}
                    onSaved={refresh}
                  />
                </section>
              );
            })}
        </div>
      )}
      {!heat ? (
        <section className="run-card">
          <h2>No heats yet</h2>
          {admin && (
            <Link to={`/competition/${competitionId}/setup?section=format`}>Create a heat →</Link>
          )}
        </section>
      ) : (
        <>
          <section className="run-card">
            <header>
              <div>
                <p>
                  {heat.division}
                  {state.features.areas && heat.area ? ` · ${heat.area}` : ""}
                </p>
                <h2>{heat.name}</h2>
                {roundsEnabled && heat.round_id && (
                  <p>{state.rounds?.find((round) => round.id === heat.round_id)?.name}</p>
                )}
                {capabilities.schedule && (
                  <p>
                    {scheduleLabel(
                      scheduleItems(state.schedule, heat.area_id).find(
                        (item) => item.id === heat.id,
                      ),
                      state.schedule?.timezone,
                    )}
                  </p>
                )}
              </div>
              {showHeatMenu && (
                <button aria-label="Heat actions" onClick={() => setSheet({ kind: "heat" })}>
                  ⋯
                </button>
              )}
            </header>
            <p className={`run-status run-status-${heat.status}`}>
              {allComplete
                ? "All heats complete"
                : heat.status[0].toUpperCase() + heat.status.slice(1)}
            </p>
            {heat.tie_pending && (official || roles.includes("starter")) && (
              <Link to={`/competition/${competitionId}/ties/heat/${heat.id}`}>Tie — resolve</Link>
            )}
            {heat.status === "live" && marshal && heat.panel_missing.length > 0 && (
              <button
                className="run-panel"
                onClick={() => setSheet({ kind: "excuse", seat: heat.panel_missing[0] })}
              >
                Panel {heat.judge_count - heat.panel_missing.length}/{heat.judge_count}
              </button>
            )}
          </section>
          {official && heat.judging.judge_placements && heat.placement_pending && (
            <button
              disabled={!online || busy}
              className="run-panel"
              onClick={() => {
                const action = officialActions.find((a) => a.label === "Placements");
                if (action) setCustom(action.render(() => setCustom(null), refresh));
              }}
            >
              Placements — {heat.placement_missing?.length ?? 0} missing
            </button>
          )}
          <section className="run-controls" aria-label="Heat controls">
            {heat.status === "complete" && official ? (
              <button
                disabled={!online || busy}
                className="run-primary"
                onClick={() => setSheet({ kind: "reopen" })}
              >
                Reopen heat
              </button>
            ) : (
              <button
                className="run-primary"
                disabled={busy || heat.status !== "pending"}
                onClick={() => void command("start")}
              >
                Start heat
              </button>
            )}
            <button
              className="run-primary"
              disabled={busy || heat.status !== "live" || !nextRider}
              onClick={() =>
                nextRider && void command("focus", { heat_rider_id: nextRider.heat_rider_id })
              }
            >
              Next rider
            </button>
            <button
              className="run-danger"
              disabled={busy || heat.status !== "live"}
              onClick={() =>
                heat.end_requires_choice !== false ? setSheet({ kind: "end" }) : void command("end")
              }
            >
              End heat
            </button>
          </section>
          {heat.status === "complete" && nextHeat && (
            <button className="run-next" onClick={() => setSelected(nextHeat.id)}>
              Next: {nextHeat.name} ▸
            </button>
          )}
          {allComplete && admin && state.status !== "complete" && (
            <button
              disabled={!online || busy}
              onClick={() =>
                setCustom(
                  integration.completeCompetition ? (
                    integration.completeCompetition(() => setCustom(null), refresh)
                  ) : (
                    <CompleteCompetitionSheet
                      competitionId={competitionId}
                      transport={transport}
                      close={() => setCustom(null)}
                      saved={refresh}
                    />
                  ),
                )
              }
            >
              Complete competition…
            </button>
          )}
          {heat.format?.jam?.mode === "queue_best_n" && (
            <JamQueue
              competitionId={competitionId}
              principalKey={state.principal}
              heatId={heat.id}
              version={heat.version}
              bestN={heat.format.jam.best_n ?? 1}
              transport={transport}
              riders={heat.riders.map((rider) => ({
                id: rider.heat_rider_id,
                name: rider.name,
                attempts: rider.attempts,
              }))}
              online={online}
              disabled={busy || !marshal || !["pending", "live"].includes(heat.status)}
              queued={ops
                .filter((op) => op.heat_id === heat.id && op.op === "jam-append")
                .map((op) => ({
                  attempt_id: op.client_id,
                  heat_rider_id: String(op.payload.heat_rider_id),
                  label: String(op.payload.label),
                  item_no: Number(op.payload.item_no),
                  queued: true,
                }))}
              append={async (payload) => {
                const result = await command("jam-append", payload);
                if (!result)
                  throw new Error("Attempt was not added. Review the server state and try again.");
              }}
            />
          )}
          <ol className="run-riders" aria-label="Riders">
            {heat.riders.map((rider) => (
              <li
                key={rider.heat_rider_id}
                className={rider.heat_rider_id === heat.focus_heat_rider_id ? "run-focused" : ""}
              >
                <span className="run-bib">{rider.bib ?? "—"}</span>
                <div className="run-rider-info">
                  <strong>{rider.name}</strong>
                  <div>
                    <span className="run-attempt">
                      {rider.current_attempt ? `Attempt ${rider.current_attempt}` : "Not started"}
                    </span>
                    {rerideLabel(
                      rider.attempts.find((a) => a.attempt_number === rider.current_attempt),
                    ) && (
                      <span>
                        {rerideLabel(
                          rider.attempts.find((a) => a.attempt_number === rider.current_attempt),
                        )}
                      </span>
                    )}
                    <span>
                      {rider.status !== "ok"
                        ? rider.status
                        : rider.heat_rider_id === heat.focus_heat_rider_id && heat.status === "live"
                          ? `Riding${heat.focus_source && heat.focus_source !== "starter" ? ` · ${heat.focus_source.replaceAll("_", " ")}` : ""}`
                          : rider.status_label || "Ready"}
                    </span>
                  </div>
                  {rider.repair_until && (
                    <RepairClock
                      deadline={rider.repair_until}
                      serverTime={state.server_time}
                      receivedAt={state.clock_received_at}
                    />
                  )}
                </div>
                {(heat.max_attempts > 1 || rider.next_attempt_number != null) && (
                  <button
                    disabled={busy || heat.status !== "live" || rider.next_attempt_number == null}
                    aria-label={`Advance attempt for ${rider.name}`}
                    onClick={() => void move(rider, true)}
                  >
                    Attempt ▶
                  </button>
                )}
                <button
                  aria-label={`Actions for ${rider.name}`}
                  onClick={() => setSheet({ kind: "rider", rider })}
                >
                  ⋯
                </button>
              </li>
            ))}
          </ol>
        </>
      )}
      {sheet && (
        <RunSheet
          title={
            sheet.kind === "end"
              ? "End heat?"
              : sheet.kind === "rider"
                ? sheet.rider.name
                : sheet.kind === "abandon"
                  ? "Abandon heat"
                  : sheet.kind === "reopen"
                    ? "Reopen heat?"
                    : sheet.kind === "excuse"
                      ? `Excuse Judge ${sheet.seat?.judge_number} for this heat`
                      : sheet.kind === "heat"
                        ? "Heat actions"
                        : "Run the day menu"
          }
          close={close}
        >
          {sheet.kind === "end" && (
            <>
              <p>Some runs may be unfinished. Choose how they count.</p>
              <button
                className={heat?.judging.force_end_default === "void" ? undefined : "run-primary"}
                data-autofocus={heat?.judging.force_end_default !== "void" || undefined}
                disabled={busy}
                onClick={() => void command("end", { force: true, open_attempts: "count" })}
              >
                Count completed runs
              </button>
              <button
                className={heat?.judging.force_end_default === "void" ? "run-primary" : undefined}
                data-autofocus={heat?.judging.force_end_default === "void" || undefined}
                disabled={busy}
                onClick={() => void command("end", { force: true, open_attempts: "void" })}
              >
                Void open runs
              </button>
              <button onClick={close}>Cancel</button>
              <p>Scores stay in the audit history.</p>
            </>
          )}
          {sheet.kind === "rider" && heat && (
            <>
              {sheet.rider.current_attempt > 0 &&
                sheet.rider.previous_attempt_number !== undefined && (
                  <button
                    disabled={busy || heat.status !== "live"}
                    onClick={() => void move(sheet.rider, false)}
                  >
                    Attempt ◀ (revert)
                  </button>
                )}
              <button
                disabled={busy || heat.status !== "live"}
                onClick={() => {
                  const rider = sheet.rider;
                  close();
                  setCustom(
                    <FallSheet
                      heat={heat}
                      rider={rider}
                      close={() => setCustom(null)}
                      record={(payload) => command("fall", payload)}
                    />,
                  );
                }}
              >
                Fall
              </button>
              {(
                [
                  ["DNS", "dns"],
                  ["Withdraw", "withdrawn"],
                  ["Injured", "injured"],
                  ["Reset status", "ok"],
                ] as const
              ).map(([label, status]) => (
                <button
                  key={status}
                  disabled={busy || heat.status !== "live"}
                  onClick={() =>
                    void command("status", {
                      heat_rider_id: sheet.rider.heat_rider_id,
                      status,
                      expected_version: sheet.rider.version ?? 0,
                    })
                  }
                >
                  {label}
                </button>
              ))}
              {heat.judging.modifiers &&
                state.modifiers?.some((d) => d.applicable_by.includes("starter")) && (
                  <button
                    disabled={!online || busy || heat.status !== "live"}
                    onClick={() => {
                      const rider = sheet.rider;
                      close();
                      setCustom(
                        <ModifierSheet
                          competitionId={competitionId}
                          heat={heat}
                          rider={rider}
                          transport={transport}
                          onClose={() => setCustom(null)}
                          onSaved={refresh}
                          definitions={(state.modifiers ?? []).filter((d) =>
                            d.applicable_by.includes("starter"),
                          )}
                        />,
                      );
                    }}
                  >
                    Apply modifier
                  </button>
                )}
              {heat.judging.re_rides?.enabled && (
                <button
                  disabled={!online || busy || heat.status !== "live"}
                  onClick={() => {
                    const rider = sheet.rider;
                    close();
                    setCustom(
                      <RerideSheet
                        competitionId={competitionId}
                        heat={heat}
                        rider={rider}
                        transport={transport}
                        onClose={() => setCustom(null)}
                        onSaved={refresh}
                      />,
                    );
                  }}
                >
                  Grant re-ride
                </button>
              )}
              {official &&
                actionsFor(heat, sheet.rider).map((action) => (
                  <button
                    key={action.label}
                    disabled={!online || busy}
                    onClick={() => {
                      close();
                      setCustom(action.render(() => setCustom(null), refresh));
                    }}
                  >
                    {action.label}
                  </button>
                ))}
            </>
          )}
          {sheet.kind === "heat" && heat && (
            <>
              {canAbandon && ["pending", "live"].includes(heat.status) && (
                <button onClick={() => setSheet({ kind: "abandon" })}>Abandon heat</button>
              )}
              {state.features.timer && heat.timer && heat.status === "live" && (
                <>
                  <p>
                    {Math.max(0, Math.ceil(heat.timer.remaining_s))} s remaining
                    {ops.length > 0 ? " · local" : ""}
                  </p>
                  <button
                    disabled={busy}
                    onClick={() => void command(heat.timer?.running ? "pause" : "resume")}
                  >
                    {heat.timer.running ? "Pause" : "Resume"}
                  </button>
                  <button disabled={busy} onClick={() => void command("adjust", { delta_s: 30 })}>
                    +30 s
                  </button>
                </>
              )}
              {officialActions.map((action) => (
                <button
                  key={action.label}
                  disabled={!online || busy}
                  onClick={() => {
                    close();
                    setCustom(action.render(() => setCustom(null), refresh));
                  }}
                >
                  {action.label}
                </button>
              ))}
            </>
          )}
          {(sheet.kind === "abandon" || sheet.kind === "excuse") && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (reason.trim())
                  void command(
                    sheet.kind === "abandon" ? "abandon" : "excuse",
                    sheet.kind === "abandon"
                      ? { reason: reason.trim() }
                      : {
                          seat_id: sheet.seat?.seat_id,
                          scope: "all",
                          excused: true,
                          reason: reason.trim(),
                        },
                  );
              }}
            >
              <label>
                Reason
                <textarea
                  autoFocus
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={2000}
                />
              </label>
              <button className="run-primary" disabled={busy || !reason.trim()} type="submit">
                {sheet.kind === "abandon" ? "Abandon heat" : "Excuse judge"}
              </button>
            </form>
          )}
          {sheet.kind === "reopen" && (
            <>
              <p>Judge edits will reopen for this heat.</p>
              <label>
                Reason
                <textarea
                  required
                  maxLength={2000}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <button
                className="run-primary"
                disabled={!online || busy || !reason.trim()}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await transport.post(`${base}/heats/${heat?.id}/reopen`, {
                      expected_version: heat?.version,
                      reason: reason.trim(),
                    });
                    close();
                    await refresh();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : "Could not reopen heat");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Reopen heat
              </button>
              <button onClick={close}>Cancel</button>
            </>
          )}
          {sheet.kind === "menu" && (
            <>
              <label className="run-toggle">
                <input
                  type="checkbox"
                  checked={sound}
                  onChange={(e) => setSound(e.target.checked)}
                />{" "}
                Sound
              </label>
              {heat && marshal && (
                <button
                  disabled={!online}
                  onClick={() => {
                    close();
                    setCustom(
                      integration.heatRiders ? (
                        integration.heatRiders(heat, () => setCustom(null), refresh)
                      ) : (
                        <HeatRidersSheet
                          competitionId={competitionId}
                          heat={heat}
                          transport={transport}
                          onClose={() => setCustom(null)}
                          onSaved={refresh}
                          remove={(path) => transport.delete(path)}
                        />
                      ),
                    );
                  }}
                >
                  Heat riders
                </button>
              )}
              {admin && (
                <Link to={`/competition/${competitionId}/setup?section=format`}>
                  Edit heats in Setup
                </Link>
              )}
              {(admin || state.seat != null) && (
                <Link to={`/competition/${competitionId}/judge`}>Judge</Link>
              )}
              {state.features.public_results && state.slug && state.status !== "draft" && (
                <div>
                  <a
                    href={`/results/${encodeURIComponent(state.slug)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Public results QR
                  </a>
                  <div role="img" aria-label="Public results QR code">
                    <QRCodeSVG
                      value={`${location.origin}/results/${encodeURIComponent(state.slug)}`}
                      size={160}
                      marginSize={4}
                    />
                  </div>
                </div>
              )}
              {state.features.registration && (
                <Link to={`/competition/${competitionId}/checkin`}>Check-in</Link>
              )}
              {state.account && <Link to={`/competition/${competitionId}`}>Overview</Link>}
              {onSignOut && (
                <button
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void onSignOut()
                      .catch((e) =>
                        setError(e instanceof Error ? e.message : "Could not leave the session."),
                      )
                      .finally(() => setBusy(false));
                  }}
                >
                  Leave session
                </button>
              )}
            </>
          )}
        </RunSheet>
      )}
      {custom}
    </main>
  );
}
